import {
  propOr,
  prop,
  compose,
  curriedFlatMap,
  curriedMap,
  curriedLength,
  curriedUniq,
  promisifyObject,
} from '@adjust/dashboard-core/lib/fp';

import httpFactory from '@adjust/dashboard-core/api/instance/http-factory';
import { PartnerName } from '@api/types';

const OAUTH_SERVICE_PARTNERS = [PartnerName.facebook, PartnerName.google];

const partnerToHttpMap = new Map();

const getBaseURLByPartnerKey = partner =>
  OAUTH_SERVICE_PARTNERS.includes(partner) ? configurationURL.adAccounts.baseUrl : configurationURL.dashboard.baseUrl;

const getHttp = partner => {
  if (!partnerToHttpMap.has(partner)) {
    const http = httpFactory({
      baseURL: `${getBaseURLByPartnerKey(partner)}/api/settings`,
    });

    partnerToHttpMap.set(partner, http);
  }

  return partnerToHttpMap.get(partner);
};

/**
 * @returns {Promise<[TwitterAdAccount]>}
 */
export const getTwitterAccounts = () => _getAccounts('twitter_ads').then(propOr([], 'twitter_accounts'));

/**
 * @returns {Promise<[FacebookAdAccount]>}
 */
export const getFacebookAccounts = () => _getAccounts('facebook').then(propOr([], 'facebook_credentials'));

/**
 * @returns {Promise<[GoogleAdAccount]>}
 */
export const getGoogleAccounts = () => _getAccounts('google').then(propOr([], 'google_credentials'));

/**
 * @returns {Promise<[[String, Number]]>}
 */
export const getAccountsCount = () =>
  promisifyObject({
    twitter: _requestAndCount(getTwitterAccounts, 'twitter_ads_accounts', 'account_id'),
    facebook: _requestAndCount(getFacebookAccounts, 'facebook_ads_accounts', 'account_id'),
    google: _requestAndCount(getGoogleAccounts, 'customer_accounts', 'customer_id'),
  });

/**
 * @param {String} partner
 * @returns {Promise<[AdAccount]>}
 * @private
 */
export const _getAccounts = partner =>
  getHttp(partner)
    .get(`/${partner}/accounts`, {
      ignore404: true,
    })
    .then(resp => resp.data)
    .catch(() => []);

/**
 * @param {Function} request
 * @param {String} subAccountsKey
 * @param {String} idKey
 * @returns {Number}
 * @private
 */
export const _requestAndCount = (request, subAccountsKey, idKey) =>
  request().then(_countSubAccounts(subAccountsKey, idKey));

/**
 * @sig (String, String) -> [AdAccount] -> Number
 * @param {String} subAccountsKey
 * @param {String} idKey
 * @param {[AdAccount]}
 * @returns {Number}
 * @private
 */
export const _countSubAccounts = (subAccountsKey, idKey) =>
  compose(curriedLength, curriedUniq, curriedMap(prop(idKey)), curriedFlatMap(prop(subAccountsKey)));
